'use client'

import { useEffect, useMemo } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { AvailabilityMeetingPlace } from '@/core/graphql/types'

import { useBooking } from '../../model/bookingContext'
import { BookingStepEnum } from '../../model/bookingTypes'
import { usePayment } from '../../model/usePayment'
import { AuthenticationGate } from '../AuthenticationGate'
import { BookingStep } from './BookingStep'
import { PaymentSummary } from './PaymentSummary'
import { PaymentStatus } from './PaymentStatus'
import { Summary } from './Summary'
import { IncompletedStepLabel } from './IncompletedStepLabel'

// Define the types to match the expected interfaces
interface OrderData {
  _id: string
  referenceId: string
  meetingPlace: {
    placeId: AvailabilityMeetingPlace
    description: string
  }
  startDate: string
  speciality: string
  price: number
}

interface PaymentDetails {
  basePrice: number
  finalPrice: number
  paymentReceiver?: string
}

// Define the PaymentStatus type based on the GraphQL schema
interface PaymentStatusResponse {
  paid: boolean
  sessionId?: string | null
  receiptUrl?: string | null
  // Add any additional fields that might be returned from the backend
}

export function PaymentStep() {
  const { goToStep, isStepCompleted, state } = useBooking()
  const isCompleted = isStepCompleted(BookingStepEnum.PAYMENT)

  if (state.currentStep === BookingStepEnum.PAYMENT) {
    return (
      <BookingStep className="sm:!rounded-br-none">
        <BookingStep.Title>
          <Trans>Payment</Trans>
        </BookingStep.Title>
        <BookingStep.Content>
          <AuthenticationGate returnPath={`/meeting-links/${state.meetingLinkSlug}/booking`} onAuthenticationRequired={() => {}}>
            <Payment meetingLink={state.meetingLinkSlug} />
          </AuthenticationGate>
        </BookingStep.Content>
      </BookingStep>
    )
  }

  if (isCompleted) {
    return <Summary icon="💳" label={t`Payment`} onEdit={() => goToStep(BookingStepEnum.PAYMENT)} text={state.paymentCompleted ? t`Paid` : t`Unpaid`} />
  }

  return (
    <IncompletedStepLabel icon="💳">
      <Trans>Payment</Trans>
    </IncompletedStepLabel>
  )
}

function Payment({ meetingLink }: { meetingLink: string }) {
  const { goToPreviousStep, state, dispatch } = useBooking()

  // Add console logs to track authentication state
  // useEffect(() => {
  //   console.log('🔍 [ORDER TRACKING] Payment page - Auth status:', status)
  //   console.log('🔍 [ORDER TRACKING] Payment page - Session data:', session)
  //   console.log('🔍 [ORDER TRACKING] Payment page - Order ID:', state.orderId)
  //   console.log('🔍 [ORDER TRACKING] Payment page - Local Order ID:', localOrderId)

  //   // Check if we're being signed out unexpectedly
  //   if (status === 'unauthenticated' && !recoveryAttemptedRef.current) {
  //     const orderIdToRecover = state.orderId || localOrderId || localStorage.getItem('paymentOrderId')

  //     if (orderIdToRecover) {
  //       console.log('🔍 [ORDER TRACKING] WARNING: User is unauthenticated but has order ID:', orderIdToRecover)

  //       // Try to recover by redirecting to the expertise information page
  //       // This prevents the automatic sign-out loop
  //       if (typeof window !== 'undefined') {
  //         const hasAttemptedRecovery = sessionStorage.getItem('paymentPageRecoveryAttempted')
  //         if (!hasAttemptedRecovery) {
  //           console.log('🔍 [ORDER TRACKING] Attempting to recover from unexpected sign-out')
  //           sessionStorage.setItem('paymentPageRecoveryAttempted', 'true')
  //           recoveryAttemptedRef.current = true

  //           // Store the order ID in multiple places to ensure it survives
  //           sessionStorage.setItem('recoveredOrderId', orderIdToRecover)
  //           localStorage.setItem('recoveredOrderId', orderIdToRecover)

  //           // Add a timestamp to prevent infinite loops
  //           sessionStorage.setItem('recoveryTimestamp', Date.now().toString())

  //           // Redirect back to expertise information page
  //           // router.push(`/meeting-links/${meetingLink}/booking/expertise-information`)
  //         }
  //       }
  //     }
  //   }

  //   // Clear recovery flag when authenticated
  //   if (status === 'authenticated' && typeof window !== 'undefined' && !isUpdatingStateRef.current) {
  //     // Check for recovered order ID and restore it
  //     const recoveredId =
  //       sessionStorage.getItem('recoveredOrderId') ||
  //       localStorage.getItem('recoveredOrderId') ||
  //       sessionStorage.getItem('paymentOrderId') ||
  //       localStorage.getItem('paymentOrderId')

  //     if (recoveredId && !state.orderId && recoveredId !== previousOrderIdRef.current) {
  //       console.log('🔍 [ORDER TRACKING] Payment page - Restoring recovered order ID:', recoveredId)
  //       isUpdatingStateRef.current = true
  //       dispatch({ type: 'SET_ORDER_ID', payload: recoveredId })
  //       previousOrderIdRef.current = recoveredId
  //       setTimeout(() => {
  //         isUpdatingStateRef.current = false
  //       }, 100)
  //     }

  //     // Clear all recovery flags
  //     sessionStorage.removeItem('paymentPageRecoveryAttempted')
  //     recoveryAttemptedRef.current = false

  //     // Don't remove these until we're sure everything is working
  //     // sessionStorage.removeItem('recoveredOrderId');
  //     // localStorage.removeItem('recoveredOrderId');
  //   }
  // }, [status, session, state.orderId, localOrderId, meetingLink, router, dispatch])

  // Get orderId from context
  const { orderId, orderReferenceId } = state

  // Initialize payment hook
  const { isLoading: isPaymentLoading, isProcessing, error: paymentError, handlePayment, paymentStatus, paymentDetails } = usePayment(meetingLink)

  // Transform orderData to match the expected type - only when data changes
  const orderDataTransform: OrderData | null = useMemo(() => {
    if (!orderId || !orderReferenceId) return null

    return {
      _id: orderId,
      referenceId: orderReferenceId,
      meetingPlace: {
        placeId: (state.meetingPlace || 'VISIO_CONFERENCE') as AvailabilityMeetingPlace,
        description: state.meetingPlaceDescription || '',
      },
      startDate: state.startDate || new Date().toISOString(),
      speciality: state.selectedSpeciality || '',
      price: paymentDetails?.finalPrice || 0,
    }
  }, [orderId, state.meetingPlace, state.meetingPlaceDescription, state.startDate, state.selectedSpeciality, paymentDetails?.finalPrice, orderReferenceId])

  // Transform paymentDetails to match the expected type
  const paymentDetailsTransform = useMemo<PaymentDetails | null>(() => {
    if (!paymentDetails) return null

    return {
      basePrice: paymentDetails.basePrice || 0,
      finalPrice: paymentDetails.finalPrice || 0,
      paymentReceiver:
        typeof paymentDetails.paymentReceiver === 'object' && paymentDetails.paymentReceiver?.stripe ? paymentDetails.paymentReceiver.stripe : undefined,
    }
  }, [paymentDetails])

  // Go to next step when payment is completed
  useEffect(() => {
    if (paymentStatus?.paid) {
      dispatch({ type: 'SET_PAYMENT_COMPLETED', payload: true, goToNextStep: true })
    }
  }, [paymentStatus?.paid, dispatch])

  // Show loading state while loading payment details
  if (isPaymentLoading) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-400 border-t-white" />
          </div>
          {/* <h3 className="mb-1 text-lg font-semibold">{t('loading')}</h3>
            <p className="text-gray-500">{t('loading_details')}</p> */}
        </div>
      </div>
    )
  }

  if (paymentError) {
    return <PaymentStatus status="error" message={paymentError.message} onRetry={handlePayment} />
  }

  if (paymentStatus?.paid) {
    return <PaymentStatus status="success" message={t`Payment successful`} receiptUrl={paymentStatus.receiptUrl} />
  }

  if (orderDataTransform && paymentDetailsTransform) {
    return <PaymentSummary orderData={orderDataTransform} paymentDetails={paymentDetailsTransform} onPay={handlePayment} isProcessing={isProcessing} />
  }

  return (
    <div className="text-center p-4">
      <p className="text-red-500 mb-4">{t`Payment details not found`}</p>
      <p className="mb-4">{t`Return to previous step`}</p>
      <button
        onClick={() => {
          goToPreviousStep()
        }}
        className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark"
      >
        {t`Go back`}
      </button>
    </div>
  )
}
